import { useState, useRef, useEffect } from "react";

import clsx from "clsx";

import CollapseIconSvg from "~/assets/images/collapse-icon.svg";

import styles from "./Collapsible.module.scss";

// Global registry to track collapsible groups
const collapseGroups = new Map();

/**
 * Props:
 * - title: string | ReactNode (header content)
 * - defaultOpen: boolean (initial open state)
 * - duration: number (animation duration in ms)
 * - easing: string (CSS easing function)
 * - passive: boolean (if true, renders content directly without collapsible behavior)
 * - collapseGroupKey: string (if provided, only one item in this group can be open at a time)
 */
export default function Collapsible({
  title,
  children,
  defaultOpen = false,
  duration = 300,
  easing = "ease",
  showCollapseIcon = true,
  showTopBorder = false,
  showBottomBorder = true,
  passive = false,
  collapseGroupKey,
  onOpen, // Callback when this collapsible opens
  href,
  className = "",
  innerClassName = "",
  recalcToken, // breakpoint değişince dışarıdan artan sayı veriyoruz; açık ise yeniden ölç
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [height, setHeight] = useState(defaultOpen ? "auto" : "0px");
  // Overflow will be managed directly by the animation logic, not a separate state
  // const [overflow, setOverflow] = useState(defaultOpen ? 'visible' : 'hidden');
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef(null);
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));
  const animationTimeoutRef = useRef(null);

  // Register/unregister with group
  useEffect(() => {
    if (collapseGroupKey) {
      if (!collapseGroups.has(collapseGroupKey)) {
        collapseGroups.set(collapseGroupKey, new Set());
      }
      const idValue = instanceId.current; // capture for cleanup
      collapseGroups.get(collapseGroupKey).add({
        id: idValue,
        setIsOpen,
      });

      return () => {
        const group = collapseGroups.get(collapseGroupKey);
        if (group) {
          group.forEach((item) => {
            if (item.id === idValue) {
              group.delete(item);
            }
          });
          if (group.size === 0) {
            collapseGroups.delete(collapseGroupKey);
          }
        }
      };
    }
  }, [collapseGroupKey]);

  // Handle group collapse logic
  const handleToggle = () => {
    if (isAnimating || href) return;

    const newOpenState = !isOpen;

    if (newOpenState && collapseGroupKey) {
      // Close other items in the same group
      const group = collapseGroups.get(collapseGroupKey);
      if (group) {
        group.forEach((item) => {
          if (item.id !== instanceId.current) {
            item.setIsOpen(false);
          }
        });
      }
    }

    setIsOpen(newOpenState);
    if (newOpenState && onOpen) {
      onOpen();
    }

    // Ek güvenlik: bazı edge-case'lerde (ör. ardışık hızlı tıklama / concurrent render)
    if (newOpenState && collapseGroupKey) {
      queueMicrotask(() => {
        const group = collapseGroups.get(collapseGroupKey);
        if (group) {
          group.forEach((item) => {
            if (item.id !== instanceId.current) {
              item.setIsOpen(false);
            }
          });
        }
      });
    }
  };

  useEffect(() => {
    if (!contentRef.current) return;

    clearTimeout(animationTimeoutRef.current); // Clear any existing animation timeout

    if (isOpen) {
      setIsAnimating(true);
      contentRef.current.style.overflow = "hidden";
      const scrollHeight = contentRef.current.scrollHeight;
      setHeight(`${scrollHeight}px`);

      animationTimeoutRef.current = setTimeout(() => {
        setHeight("auto");
        if (contentRef.current) {
          // Check if ref still exists
          contentRef.current.style.overflow = "visible";
        }
        setIsAnimating(false);
      }, duration);
    } else {
      setIsAnimating(true);
      // If height is 'auto', get current scrollHeight before setting to 0
      const currentHeight = contentRef.current.scrollHeight;
      setHeight(`${currentHeight}px`);

      if (contentRef.current) {
        contentRef.current.style.overflow = "hidden";
      }

      // Force a reflow before setting height to 0 for smooth transition
      requestAnimationFrame(() => {
        setHeight("0px");
        animationTimeoutRef.current = setTimeout(() => {
          setIsAnimating(false);
          // Overflow remains hidden when closed
        }, duration);
      });
    }

    return () => clearTimeout(animationTimeoutRef.current);
  }, [isOpen, duration]); // Removed height from dependencies

  // recalcToken değişmişse ve open ise maxHeight'i yeniden ayarla (ör. breakpoint ile içerik genişliği değişti)
  useEffect(() => {
    if (!contentRef.current) return;
    if (!isOpen) return; // sadece açıkken
    const scrollHeight = contentRef.current.scrollHeight;
    setHeight(`${scrollHeight}px`);
    // kısa sürede auto'ya geri al ki future content height değişimlerinde sorun olmasın
    const id = setTimeout(() => {
      if (contentRef.current) {
        setHeight("auto");
        contentRef.current.style.overflow = "visible";
      }
    }, duration);
    return () => clearTimeout(id);
  }, [recalcToken, isOpen, duration]);

  if (passive) {
    return (
      <div
        className={clsx(
          styles.accordion,
          {
            [styles.withTopBorder]: false,
            [styles.accordionPassive]: true,
          },
          className
        )}
      >
        <div className={clsx(styles.header, styles.passive)}>
          <span className={styles.title}>{title}</span>
        </div>
        <div className={styles.content}>
          <div className={styles.inner}>{children}</div>
        </div>
      </div>
    );
  }
  const WrapperDom = href ? "a" : "button";
  return (
    <div
      className={clsx(
        styles.accordion,
        {
          collapsible: true,
          [styles.withTopBorder]: showTopBorder,
          [styles.withBottomBorder]: showBottomBorder,
          [styles.linkMode]: !!href,
        },
        className
      )}
    >
      {/* Header */}
      <WrapperDom
        type="button"
        className={clsx(styles.header, "Collapsible-button", { open: isOpen })}
        onClick={handleToggle}
        href={href}
      >
        <span
          className={clsx(styles.title, {
            [styles.titleOpen]: isOpen,
            "Collapsible-title": true,
            "Collapsible-title--open": isOpen,
          })}
        >
          {title}
        </span>
        {showCollapseIcon && (
          <CollapseIconSvg
            className={clsx(styles.collapseIcon, "Collapsible-icon", isOpen && styles.collapseIconOpen)}
          />
        )}
      </WrapperDom>
      <div
        ref={contentRef}
        className={clsx(styles.content, { open: isOpen, "Collapsible-content": true })}
        style={{
          maxHeight: height,
          // overflow is now managed directly on contentRef.current.style
          transition: `max-height ${duration}ms ${easing}, opacity ${duration * 0.8}ms ${easing}`,
        }}
      >
        <div className={clsx(styles.inner, innerClassName)}>{children}</div>
      </div>
    </div>
  );
}
